/**
 * SuperOrgsTab — All organisations (ATOMS / OEM / Distributor).
 * atoms_admin can create/edit any org and manage machine shares for distributors.
 */
import React, { useEffect, useState } from 'react';
import {
  Stack, Group, Button, TextInput, Table, Badge, ActionIcon,
  Text, Loader, Alert, Tooltip, Accordion, ThemeIcon,
  Drawer, Divider, Switch,
} from '@mantine/core';
import {
  MdEdit, MdAddBusiness, MdSearch, MdRefresh,
  MdBusiness, MdStore, MdSecurity, MdShare,
} from 'react-icons/md';
import {
  getOrgs, getMachines, getMachineShares, createShare, deleteShare, updateOrg,
} from '../../API/adminAPI';
import OrgWizard from './OrgWizard';

const ATOMS_BLUE = '#1F4E79';

const typeIcon  = { atoms: <MdSecurity size={14} />, oem: <MdBusiness size={14} />, distributor: <MdStore size={14} /> };
const typeColor = { atoms: 'violet', oem: 'blue', distributor: 'teal' };
const typeLabel = { atoms: 'ATOMS', oem: 'OEM', distributor: 'Distributor' };

/* ── Machine share modal (used when a distributor row is clicked) ───────────── */
const ShareModal = ({ org, onClose }) => {
  const [machines, setMachines]   = useState([]);
  const [shares, setShares]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const loadShares = async (machList) => {
    const list = machList || machines;
    try {
      const results = await Promise.all(list.map((m) => getMachineShares(m.id).then((sd) => ({ m, sd }))));
      const allShares = [];
      for (const { m, sd } of results) {
        for (const s of (sd.shares || [])) {
          if (s.to_org_id === org.id) allShares.push({ ...s, machine: m });
        }
      }
      setShares(allShares);
    } catch { setShares([]); }
  };

  useEffect(() => {
    getMachines()
      .then((md) => {
        const list = md.machines || [];
        setMachines(list);
        return loadShares(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sharedIds      = new Set(shares.map((s) => s.machine.id));
  const unsharedMachines = machines.filter((m) => !sharedIds.has(m.id));

  const addShare = async (machineId) => {
    setSaving(true);
    try {
      await createShare(machineId, { to_org: org.id, permitted_channels: [], is_active: true });
      await loadShares(machines);
    } catch (e) {
      setError(e?.response?.data?.error || 'Share failed');
    } finally { setSaving(false); }
  };

  const removeShare = async (share) => {
    setSaving(true);
    try {
      await deleteShare(share.machine.id, share.id);
      await loadShares(machines);
    } catch (e) {
      setError(e?.response?.data?.error || 'Remove failed');
    } finally { setSaving(false); }
  };

  return (
    <Drawer
      opened
      onClose={() => onClose()}
      title={
        <Group spacing={8}>
          <Badge variant="light" color="teal" size="lg">{org.name}</Badge>
          <Text weight={600} size="md">Machine Shares</Text>
        </Group>
      }
      size="lg"
      position="right"
      padding="xl"
    >
      {error && <Alert color="red" mb="sm">{error}</Alert>}
      {loading ? <Loader mx="auto" mt="md" /> : (
        <Stack>
          <Text size="sm" color="dimmed">
            Machines shared to this distributor appear under their branding.
            Distributors only see non-internal channels unless further restricted.
          </Text>

          {shares.length > 0 && (
            <>
              <Text size="xs" weight={700} color="dimmed" transform="uppercase">Currently Shared</Text>
              {shares.map((s) => (
                <Group key={s.id} position="apart" px="sm" py={6}
                  sx={(t) => ({ background: t.colors.green[0], border: `1px solid ${t.colors.green[3]}`, borderRadius: t.radius.sm })}>
                  <Group spacing={8}>
                    <Badge variant="outline" color="blue" size="sm">{s.machine.machine_id}</Badge>
                    <Text size="sm" weight={500}>{s.machine.machine_name || s.machine.machine_id}</Text>
                    {!s.is_active && <Badge color="red" size="xs">Suspended</Badge>}
                  </Group>
                  <Button variant="subtle" color="red" size="xs" compact
                    loading={saving} onClick={() => removeShare(s)}>
                    Remove
                  </Button>
                </Group>
              ))}
              <Divider />
            </>
          )}

          {unsharedMachines.length > 0 && (
            <>
              <Text size="xs" weight={700} color="dimmed" transform="uppercase">Add Machine</Text>
              {unsharedMachines.map((m) => (
                <Group key={m.id} position="apart" px="sm" py={6}
                  sx={(t) => ({ border: `1px solid ${t.colors.gray[3]}`, borderRadius: t.radius.sm })}>
                  <Group spacing={8}>
                    <Badge variant="outline" color="gray" size="sm">{m.machine_id}</Badge>
                    <Text size="sm">{m.machine_name || m.machine_id}</Text>
                    {m.owned_by && <Badge size="xs" variant="light" color="blue">{m.owned_by}</Badge>}
                  </Group>
                  <Button variant="light" size="xs" compact
                    loading={saving} onClick={() => addShare(m.id)}>
                    Share
                  </Button>
                </Group>
              ))}
            </>
          )}

          {shares.length === 0 && unsharedMachines.length === 0 && (
            <Text color="dimmed" align="center" py="md">No machines available.</Text>
          )}
        </Stack>
      )}
      <Group position="right" mt="xl">
        <Button
          onClick={() => onClose()}
          sx={{ backgroundColor: ATOMS_BLUE, '&:hover': { backgroundColor: '#163a5f' } }}
        >
          Done
        </Button>
      </Group>
    </Drawer>
  );
};

/* ── Main tab ────────────────────────────────────────────────────────────────── */
const SuperOrgsTab = () => {
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [wizardOrg, setWizardOrg] = useState(undefined);
  const [shareOrg, setShareOrg]   = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const d = await getOrgs();
      setOrgs(d.organisations || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load organisations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = orgs.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {
    atoms:       filtered.filter((o) => o.org_type === 'atoms'),
    oem:         filtered.filter((o) => o.org_type === 'oem'),
    distributor: filtered.filter((o) => o.org_type === 'distributor'),
  };

  const toggleActive = async (org) => {
    try {
      await updateOrg(org.id, { is_active: !org.is_active });
      load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Toggle failed');
    }
  };

  const OrgRow = ({ o }) => (
    <tr>
      <td>
        <Group spacing={8}>
          {o.logo_url && (
            <img src={o.logo_url} alt="" style={{ height: 22, objectFit: 'contain', borderRadius: 2 }} />
          )}
          <Text size="sm" weight={500}>{o.name}</Text>
        </Group>
      </td>
      <td>
        <Group spacing={6}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: o.primary_color || ATOMS_BLUE, border: '1px solid #ccc' }} />
          <Text size="xs" color="dimmed">{o.primary_color || '—'}</Text>
        </Group>
      </td>
      <td>
        <Text size="xs" color="dimmed">{o.parent_oem || '—'}</Text>
      </td>
      <td>
        <Switch
          size="xs"
          checked={o.is_active}
          onChange={() => toggleActive(o)}
          label={<Text size="xs" color={o.is_active ? 'green' : 'red'}>{o.is_active ? 'Active' : 'Suspended'}</Text>}
        />
      </td>
      <td>
        <Group spacing={4}>
          <Tooltip label="Edit organisation">
            <ActionIcon size="sm" variant="subtle" onClick={() => setWizardOrg(o)}>
              <MdEdit />
            </ActionIcon>
          </Tooltip>
          {o.org_type === 'distributor' && (
            <Tooltip label="Manage machine shares">
              <ActionIcon size="sm" variant="subtle" color="teal" onClick={() => setShareOrg(o)}>
                <MdShare />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </td>
    </tr>
  );

  if (loading) return <Loader mt="xl" mx="auto" />;

  return (
    <Stack>
      {error && <Alert color="red">{error}</Alert>}

      <Group position="apart">
        <TextInput
          placeholder="Search organisations…"
          icon={<MdSearch />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={{ flex: 1, maxWidth: 320 }}
        />
        <Group spacing="xs">
          <ActionIcon onClick={load} variant="subtle" title="Refresh">
            <MdRefresh size={20} />
          </ActionIcon>
          <Button
            leftIcon={<MdAddBusiness />}
            onClick={() => setWizardOrg(null)}
            sx={{ backgroundColor: ATOMS_BLUE, '&:hover': { backgroundColor: '#163a5f' } }}
          >
            New Organisation
          </Button>
        </Group>
      </Group>

      <Accordion defaultValue="oem" variant="separated">
        {['atoms', 'oem', 'distributor'].map((type) =>
          grouped[type].length > 0 ? (
            <Accordion.Item key={type} value={type}>
              <Accordion.Control>
                <Group spacing={8}>
                  <ThemeIcon size="sm" color={typeColor[type]} variant="light">
                    {typeIcon[type]}
                  </ThemeIcon>
                  <Text weight={600}>{typeLabel[type]}</Text>
                  <Badge size="xs" color={typeColor[type]} variant="light">
                    {grouped[type].length}
                  </Badge>
                  {type === 'distributor' && (
                    <Text size="xs" color="dimmed">— click <MdShare style={{ verticalAlign: 'middle' }} /> to configure machine access</Text>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Table striped highlightOnHover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Brand Colour</th>
                      <th>Parent OEM</th>
                      <th>Status</th>
                      <th style={{ width: 80 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[type].map((o) => <OrgRow key={o.id} o={o} />)}
                  </tbody>
                </Table>
              </Accordion.Panel>
            </Accordion.Item>
          ) : null
        )}
      </Accordion>

      {filtered.length === 0 && !loading && (
        <Text color="dimmed" align="center" py="xl">No organisations found</Text>
      )}

      {wizardOrg !== undefined && (
        <OrgWizard
          org={wizardOrg}
          onClose={(saved) => { setWizardOrg(undefined); if (saved) load(); }}
        />
      )}

      {shareOrg && (
        <ShareModal
          org={shareOrg}
          onClose={() => { setShareOrg(null); load(); }}
        />
      )}
    </Stack>
  );
};

export default SuperOrgsTab;
